import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiGet, apiPost, apiPatch, apiDelete } from 'api'
import AuthContext from 'context/auth-context'

const customAnalyticsListKey = (municipalityId, sectionId) =>
  ['admin-custom-analytics', municipalityId, sectionId || 'all']

const customAnalyticKey = (municipalityId, id) => ['admin-custom-analytic', municipalityId, id]

const customAnalyticRunsKey = (municipalityId, sectionId) =>
  ['admin-custom-analytic-runs', municipalityId, sectionId || 'all']

const customAnalyticRunKey = (municipalityId, id) =>
  ['admin-custom-analytic-run', municipalityId, id]

const getCustomAnalytics = async (token, municipalityId, params = {}) => {
  const url = `admin/municipalities/${municipalityId}/custom_analytics`
  return apiGet({ url, token, params })
}

const getCustomAnalytic = async (token, municipalityId, id) => {
  const url = `admin/municipalities/${municipalityId}/custom_analytics/${id}`
  return apiGet({ url, token })
}

const createCustomAnalytic = async (token, municipalityId, params) => {
  const url = `admin/municipalities/${municipalityId}/custom_analytics`
  return apiPost({ url, token, objectName: 'custom_analytic', params })
}

const updateCustomAnalytic = async (token, municipalityId, id, params) => {
  const url = `admin/municipalities/${municipalityId}/custom_analytics/${id}`
  return apiPatch({ url, token, objectName: 'custom_analytic', params })
}

const deleteCustomAnalytic = async (token, municipalityId, id) => {
  const url = `admin/municipalities/${municipalityId}/custom_analytics/${id}`
  return apiDelete({ url, token })
}

const getCustomAnalyticRuns = async (token, municipalityId, params = {}) => {
  const url = `admin/municipalities/${municipalityId}/custom_analytic_runs`
  return apiGet({ url, token, params })
}

const getCustomAnalyticRun = async (token, municipalityId, id) => {
  const url = `admin/municipalities/${municipalityId}/custom_analytic_runs/${id}`
  return apiGet({ url, token })
}

const createCustomAnalyticRun = async (token, municipalityId, params) => {
  const url = `admin/municipalities/${municipalityId}/custom_analytic_runs`
  return apiPost({ url, token, objectName: 'custom_analytic_run', params })
}

const getComposablePredicates = async (token) => {
  return apiGet({ url: 'admin/composable_predicates', token })
}

const getSectionColumns = async (token, municipalityId, sectionId) => {
  const url = `admin/municipalities/${municipalityId}/section_columns`
  return apiGet({ url, token, params: { section_id: sectionId } })
}

const generateCustomAnalyticRecipe = async (token, municipalityId, params) => {
  const url = `admin/municipalities/${municipalityId}/custom_analytics/generate_recipe`
  return apiPost({ url, token, objectName: 'ai_recipe', params })
}

const mergeCustomAnalytics = async (token, municipalityId, params) => {
  const url = `admin/municipalities/${municipalityId}/custom_analytics/merge`
  return apiPost({ url, token, objectName: 'custom_analytic', params })
}

export function useCustomAnalytics({ municipalityId, sectionId, options } = {}) {
  const token = useContext(AuthContext)
  const params = sectionId ? { section_id: sectionId } : {}
  return useQuery(
    customAnalyticsListKey(municipalityId, sectionId),
    () => getCustomAnalytics(token, municipalityId, params),
    { enabled: !!municipalityId, ...options }
  )
}

export function useCustomAnalytic({ municipalityId, id, options } = {}) {
  const token = useContext(AuthContext)
  return useQuery(
    customAnalyticKey(municipalityId, id),
    () => getCustomAnalytic(token, municipalityId, id),
    { enabled: !!municipalityId && !!id, ...options }
  )
}

export function useCreateCustomAnalytic({ municipalityId, options } = {}) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  return useMutation(
    (params) => createCustomAnalytic(token, municipalityId, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['admin-custom-analytics', municipalityId])
        options?.onSuccess?.(data)
      }
    }
  )
}

// Accepts `{id, ...params}` per-mutation so a single hook instance can
// update any analytic — useful when the caller is editing an analytic
// chosen from a list and the id isn't known at render time.
export function useUpdateCustomAnalytic({ municipalityId, options } = {}) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  return useMutation(
    ({ id, ...params }) => updateCustomAnalytic(token, municipalityId, id, params),
    {
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['admin-custom-analytics', municipalityId])
        queryClient.invalidateQueries(customAnalyticKey(municipalityId, variables.id))
        options?.onSuccess?.(data, variables)
      }
    }
  )
}

export function useDeleteCustomAnalytic({ municipalityId, options } = {}) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  return useMutation(
    (id) => deleteCustomAnalytic(token, municipalityId, id),
    {
      ...options,
      onSuccess: (data, id) => {
        queryClient.invalidateQueries(['admin-custom-analytics', municipalityId])
        queryClient.invalidateQueries(customAnalyticKey(municipalityId, id))
        options?.onSuccess?.(data, id)
      }
    }
  )
}

export function useCustomAnalyticRuns({ municipalityId, sectionId, options } = {}) {
  const token = useContext(AuthContext)
  const params = sectionId ? { section_id: sectionId } : {}
  return useQuery(
    customAnalyticRunsKey(municipalityId, sectionId),
    () => getCustomAnalyticRuns(token, municipalityId, params),
    { enabled: !!municipalityId, ...options }
  )
}

export function useCustomAnalyticRun({ municipalityId, id, options } = {}) {
  const token = useContext(AuthContext)
  return useQuery(
    customAnalyticRunKey(municipalityId, id),
    () => getCustomAnalyticRun(token, municipalityId, id),
    { enabled: !!municipalityId && !!id, ...options }
  )
}

export function useCreateCustomAnalyticRun({ municipalityId, options } = {}) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  return useMutation(
    (params) => createCustomAnalyticRun(token, municipalityId, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['admin-custom-analytic-runs', municipalityId])
        options?.onSuccess?.(data)
      }
    }
  )
}

export function useComposablePredicates({ options } = {}) {
  const token = useContext(AuthContext)
  return useQuery(
    ['composable-predicates'],
    () => getComposablePredicates(token),
    // refetchOnMount: 'always' so registry edits in dev (and new deploys
    // in prod) take effect after a page navigation — with a bare staleTime
    // the cached registry would be served for the full hour. The hour-long
    // staleTime/cacheTime still prevents refetches while the page is
    // mounted (e.g. on every keystroke in the recipe builder).
    { staleTime: 60 * 60 * 1000, cacheTime: 60 * 60 * 1000, refetchOnMount: 'always', ...options }
  )
}

export function useSectionColumns({ municipalityId, sectionId, options } = {}) {
  const token = useContext(AuthContext)
  return useQuery(
    ['admin-section-columns', municipalityId, sectionId],
    () => getSectionColumns(token, municipalityId, sectionId),
    { enabled: !!municipalityId && !!sectionId, staleTime: 5 * 60 * 1000, ...options }
  )
}

export function useGenerateCustomAnalyticRecipe({ municipalityId, options } = {}) {
  const token = useContext(AuthContext)
  return useMutation(
    (params) => generateCustomAnalyticRecipe(token, municipalityId, params),
    { ...options }
  )
}

// Additive merge: composes the selected analytics into one new analytic
// (originals stay). Invalidate the list so the new analytic appears.
export function useMergeCustomAnalytics({ municipalityId, options } = {}) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  return useMutation(
    (params) => mergeCustomAnalytics(token, municipalityId, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['admin-custom-analytics', municipalityId])
        options?.onSuccess?.(data)
      }
    }
  )
}

// ---------------------------------------------------------------------------
// Municipality-user-facing variants (non-admin endpoints from the
// composable-analytics user controllers). Used by the policy-page compose
// flow, where the actor is a municipality user acting on their own muni.
// ---------------------------------------------------------------------------

const getUserComposablePredicates = async (token, municipalityId) =>
  apiGet({ url: `municipalities/${municipalityId}/composable_predicates`, token })

const getUserSectionColumns = async (token, municipalityId, sectionId) =>
  apiGet({ url: `municipalities/${municipalityId}/section_columns`, token, params: { section_id: sectionId } })

const generateUserCustomAnalyticRecipe = async (token, municipalityId, params) =>
  apiPost({ url: `municipalities/${municipalityId}/custom_analytics/generate_recipe`, token, objectName: 'ai_recipe', params })

const createUserCustomAnalytic = async (token, municipalityId, params) =>
  apiPost({ url: `municipalities/${municipalityId}/custom_analytics`, token, objectName: 'custom_analytic', params })

const getUserCustomAnalytic = async (token, municipalityId, id) =>
  apiGet({ url: `municipalities/${municipalityId}/custom_analytics/${id}`, token })

const updateUserCustomAnalytic = async (token, municipalityId, id, params) =>
  apiPatch({ url: `municipalities/${municipalityId}/custom_analytics/${id}`, token, objectName: 'custom_analytic', params })

export function useUserComposablePredicates({ municipalityId, options } = {}) {
  const token = useContext(AuthContext)
  return useQuery(
    ['user-composable-predicates', municipalityId],
    () => getUserComposablePredicates(token, municipalityId),
    { enabled: !!municipalityId, staleTime: 60 * 60 * 1000, cacheTime: 60 * 60 * 1000, ...options }
  )
}

export function useUserSectionColumns({ municipalityId, sectionId, options } = {}) {
  const token = useContext(AuthContext)
  return useQuery(
    ['user-section-columns', municipalityId, sectionId],
    () => getUserSectionColumns(token, municipalityId, sectionId),
    { enabled: !!municipalityId && !!sectionId, staleTime: 5 * 60 * 1000, ...options }
  )
}

export function useGenerateUserCustomAnalyticRecipe({ municipalityId, options } = {}) {
  const token = useContext(AuthContext)
  return useMutation(
    (params) => generateUserCustomAnalyticRecipe(token, municipalityId, params),
    { ...options }
  )
}

export function useCreateUserCustomAnalytic({ municipalityId, options } = {}) {
  const token = useContext(AuthContext)
  return useMutation(
    (params) => createUserCustomAnalytic(token, municipalityId, params),
    { ...options }
  )
}

// Fetch a single custom analytic's raw (editable) fields — name, description,
// description_template, recipe — for the edit flow. The user-facing show
// endpoint renders the same admin partial, so these are the un-resolved
// values (unlike the analytics#show payload, which resolves the description).
export function useUserCustomAnalytic({ municipalityId, id, options } = {}) {
  const token = useContext(AuthContext)
  return useQuery(
    ['user-custom-analytic', municipalityId, id],
    () => getUserCustomAnalytic(token, municipalityId, id),
    { enabled: !!municipalityId && !!id, ...options }
  )
}

export function useUpdateUserCustomAnalytic({ municipalityId, options } = {}) {
  const token = useContext(AuthContext)
  return useMutation(
    ({ id, ...params }) => updateUserCustomAnalytic(token, municipalityId, id, params),
    { ...options }
  )
}
